// BookmarksRail — left-side folder rail for /bookmarks.
// Sized for a readable folder tree (not the denser catalog facet rails).

import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import BookmarkFolderActions from '@/components/Bookmarks/BookmarkFolderActions';
import {
  buildFolderTree,
  flattenFolderTree,
  getAncestorIds,
} from '@/constants/utils/folder_tree';

const IS_WEB = Platform.OS === 'web';

export const RAIL_WIDTH = 260;
const ROW_HEIGHT = 40;
const DEPTH_INDENT = 16;

const RailItem = ({ label, count, icon, isActive, onPress }) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const accent = colors.accent_color_primary;
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        {
          backgroundColor: isActive
            ? colors.solid_color_tertiary
            : (hovered ? colors.solid_color_secondary : 'transparent'),
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <Icon
        name={icon}
        size={16}
        color={isActive ? accent : colors.text_color_secondary}
      />
      <Text
        size="s"
        weight={isActive ? 'bold' : 'medium'}
        numberOfLines={1}
        style={{ flex: 1, color: colors.text_color_primary }}
      >
        {label}
      </Text>
      {typeof count === 'number' && (
        <Text
          size="xs"
          weight="semibold"
          style={{
            color: colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count}
        </Text>
      )}
    </Pressable>
  );
};

const FolderRow = ({ folder, isActive, onPress, onToggleExpand }) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const iconName = folder.icon || 'folder';
  const accent = colors.accent_color_primary;
  const depth = folder.depth || 0;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        {
          backgroundColor: isActive
            ? colors.solid_color_tertiary
            : (hovered ? colors.solid_color_secondary : 'transparent'),
          paddingLeft: 10 + depth * DEPTH_INDENT,
        },
        IS_WEB && { cursor: 'pointer' },
      ]}
    >
      {folder.hasChildren ? (
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            onToggleExpand?.(folder.id);
          }}
          hitSlop={8}
          style={[styles.chevronHit, IS_WEB && { cursor: 'pointer' }]}
        >
          <Icon
            name={folder.isExpanded ? 'chevron-down' : 'chevron-right'}
            size={12}
            color={colors.text_color_secondary}
          />
        </Pressable>
      ) : (
        <View style={styles.chevronHit} />
      )}
      <Icon
        name={iconName}
        size={16}
        color={isActive ? accent : colors.text_color_secondary}
      />
      <Text
        size="s"
        weight={isActive ? 'bold' : 'medium'}
        numberOfLines={1}
        style={{ flex: 1, color: colors.text_color_primary }}
      >
        {folder.name}
      </Text>
      {typeof folder.itemCount === 'number' && (
        <Text
          size="xs"
          weight="semibold"
          style={{
            color: colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {folder.itemCount}
        </Text>
      )}
    </Pressable>
  );
};

const BookmarksRail = ({
  allLabel,
  allCount,
  foldersSectionLabel,
  folders,
  activeFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onChangeIconFolder,
  onDeleteFolder,
  onCreateSubfolder,
  onMoveFolder,
  t,
}) => {
  const { colors } = useTheme();
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!activeFolderId) return;
    const ancestors = getAncestorIds(folders, activeFolderId);
    if (ancestors.length === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      ancestors.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeFolderId, folders]);

  const visibleFolders = useMemo(() => {
    const tree = buildFolderTree(folders);
    return flattenFolderTree(tree, { expandedIds }).map((folder) => ({
      ...folder,
      isExpanded: expandedIds.has(folder.id),
    }));
  }, [folders, expandedIds]);

  const handleToggleExpand = (folderId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  return (
    <View
      style={[
        styles.rail,
        {
          borderRightColor: colors.border_color_secondary,
          backgroundColor: colors.solid_color_primary,
        },
      ]}
    >
      <ScrollView
        contentContainerStyle={{ paddingTop: 10, paddingBottom: 20 }}
        showsVerticalScrollIndicator={false}
      >
        <RailItem
          label={allLabel}
          count={typeof allCount === 'number' ? allCount : undefined}
          icon="bookmark"
          isActive={!activeFolderId}
          onPress={() => onSelectFolder(null)}
        />

        <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />

        <View style={styles.sectionHeader}>
          <Text size="xs" weight="bold" color="secondary" style={styles.sectionHeaderLabel}>
            {foldersSectionLabel}
          </Text>
          <Pressable
            onPress={onCreateFolder}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('pages.bookmarks.newFolder')}
            style={[
              styles.addButton,
              { backgroundColor: colors.solid_color_tertiary },
              Platform.OS === 'web' && { cursor: 'pointer' },
            ]}
          >
            <Icon name="plus" size={13} color={colors.text_color_primary} />
          </Pressable>
        </View>

        {visibleFolders.map((f) => {
          const isActive = activeFolderId === f.id;
          return (
            <React.Fragment key={f.id}>
              <FolderRow
                folder={f}
                isActive={isActive}
                onPress={() => onSelectFolder(f.id)}
                onToggleExpand={handleToggleExpand}
              />
              {isActive ? (
                <BookmarkFolderActions
                  folder={f}
                  onRename={onRenameFolder}
                  onChangeIcon={onChangeIconFolder}
                  onDelete={onDeleteFolder}
                  onCreateSubfolder={onCreateSubfolder}
                  onMove={onMoveFolder}
                  t={t}
                  compact
                  style={[styles.railFolderActions, { paddingLeft: 10 + (f.depth || 0) * DEPTH_INDENT }]}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </ScrollView>
    </View>
  );
};

BookmarksRail.propTypes = {
  allLabel: PropTypes.string.isRequired,
  allCount: PropTypes.number,
  foldersSectionLabel: PropTypes.string.isRequired,
  folders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string,
    itemCount: PropTypes.number,
    parentId: PropTypes.string,
  })).isRequired,
  activeFolderId: PropTypes.string,
  onSelectFolder: PropTypes.func.isRequired,
  onCreateFolder: PropTypes.func.isRequired,
  onRenameFolder: PropTypes.func.isRequired,
  onChangeIconFolder: PropTypes.func.isRequired,
  onDeleteFolder: PropTypes.func.isRequired,
  onCreateSubfolder: PropTypes.func,
  onMoveFolder: PropTypes.func,
  t: PropTypes.func.isRequired,
};

BookmarksRail.defaultProps = {
  activeFolderId: null,
};

const styles = StyleSheet.create({
  rail: {
    width: RAIL_WIDTH,
    borderRightWidth: StyleSheet.hairlineWidth,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    height: ROW_HEIGHT,
    marginHorizontal: 8,
    borderRadius: 8,
  },
  chevronHit: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  railFolderActions: {
    paddingHorizontal: 8,
    paddingTop: 4,
    paddingBottom: 10,
    marginHorizontal: 8,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 12,
    marginHorizontal: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 2,
    paddingBottom: 8,
  },
  sectionHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});

export default BookmarksRail;
